/**
 * Writing "where this position is open" from the market list.
 *
 * The markets are defined once, on the server (server/src/lib/fieldMarkets.ts); that list is what
 * the Location step offers and what the application accepts. The job posting used to repeat the
 * names in three sentences of its own, kept in step by hand - so adding a market meant remembering
 * every one of them, and one of them went stale. The posting's copy now carries tokens, and these
 * fill them in.
 *
 * Deliberately free of React and of any import: the copy rules are plain text in, plain text out,
 * so they can be exercised directly (see server/tests/fieldSalesMarkets.test.ts).
 */

/** A comma list of short names, ending "and": "Dallas–Fort Worth, Houston, and Chicago". */
export const MARKETS_TOKEN = '{MARKETS}';
/** A semicolon list of full names, ending "and": "Dallas–Fort Worth, TX; ...; and Chicago, IL". */
export const MARKETS_FULL_AND_TOKEN = '{MARKETS_FULL_AND}';
/** The same, ending "or" - for "you live in or near one of: ... or Chicago, IL". */
export const MARKETS_FULL_OR_TOKEN = '{MARKETS_FULL_OR}';

/** What the sentences say until the real list has arrived, so they always read as sentences. */
export const MARKETS_PENDING = 'the metro areas we cover';

/** "Dallas–Fort Worth, TX" -> "Dallas–Fort Worth" for the shorter, chattier sentence. */
export function shortMarketName(name: string): string {
  return name.replace(/,\s*[A-Z]{2}$/, '');
}

export function formatMarketList(names: string[], options: { separator?: string; conjunction?: string; short?: boolean } = {}): string {
  const { separator = ',', conjunction = 'and', short = false } = options;
  const parts = names.map((name) => (short ? shortMarketName(name) : name));
  if (parts.length === 0) return MARKETS_PENDING;
  if (parts.length === 1) return parts[0]!;
  if (parts.length === 2) return `${parts[0]} ${conjunction} ${parts[1]}`;
  return `${parts.slice(0, -1).join(`${separator} `)}${separator} ${conjunction} ${parts[parts.length - 1]}`;
}

/** Fills a piece of listing copy with the real markets, or leaves it readable without them. */
export function fillMarkets(text: string, names: string[] | null): string {
  const list = (options: Parameters<typeof formatMarketList>[1]) => (names?.length ? formatMarketList(names, options) : MARKETS_PENDING);
  return text
    .split(MARKETS_TOKEN).join(list({ short: true }))
    .split(MARKETS_FULL_AND_TOKEN).join(list({ separator: ';' }))
    .split(MARKETS_FULL_OR_TOKEN).join(list({ separator: ';', conjunction: 'or' }));
}
